import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { BiBorderAll, BiChevronDown } from "react-icons/bi";
import { BsBorder } from "react-icons/bs";
import { MdLineWeight } from "react-icons/md";
import { RiForbid2Line } from "react-icons/ri";
import { TbBorderCornerPill, TbLineDashed, TbLineDotted } from "react-icons/tb";

interface BorderData {
  lineStyle: string;
  strokeWidth: number;
  cornerRadius: number;
  stroke: string;
}

interface BorderSettingsProps {
  border: BorderData;
  onLineStyleChange: (lineStyle: string) => void;
  onStrokeWidthChange: (strokeWidth: number) => void;
  onCornerRadiusChange: (cornerRadius: number) => void;
  onColorChange: (stroke: string) => void;
}

const borderStyles = [
  { value: "none", label: "No Border", icon: RiForbid2Line },
  { value: "solid", label: "Solid Border", icon: BiBorderAll },
  { value: "dotted", label: "Dotted Border", icon: TbLineDotted },
  { value: "dashed", label: "Dashed Border", icon: TbLineDashed },
];

export function BorderSettings({
  border,
  onLineStyleChange,
  onStrokeWidthChange,
  onCornerRadiusChange,
  onColorChange,
}: BorderSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localStroke, setLocalStroke] = useState(border.stroke);
  const strokeTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    setLocalStroke(border.stroke);
  }, [border.stroke]);

  const handleColorChange = (color: string) => {
    setLocalStroke(color);
    if (strokeTimer.current) clearTimeout(strokeTimer.current);
    strokeTimer.current = setTimeout(() => {
      onColorChange(color);
    }, 80);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white/50 hover:border-primary-400 hover:bg-gray-50 transition-all duration-200"
      >
        <BsBorder className="w-5 h-5 text-gray-600" />
        <BiChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 min-w-[280px]">
          {/* Border Style */}
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Border Style
            </div>
            <div className="flex justify-center gap-2">
              {borderStyles.map((style) => {
                const isActive =
                  style.value === "none"
                    ? !border.lineStyle || border.lineStyle === "none"
                    : border.lineStyle === style.value;
                const Icon = style.icon;

                return (
                  <IconButton
                    key={style.value}
                    onClick={() => onLineStyleChange(style.value)}
                    variant="ghost"
                    aria-label={style.label}
                    className={`p-2.5 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                      isActive
                        ? "border-primary-500 bg-primary-50 shadow-sm"
                        : "border-gray-200 hover:border-primary-300 bg-white"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${isActive ? "text-primary-600" : "text-gray-500"}`}
                    />
                  </IconButton>
                );
              })}
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-100">
            {/* Border Color */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase">Color</span>
                <label className="cursor-pointer">
                  <div className="relative group">
                    <div
                      className="w-8 h-8 rounded-lg border-2 border-gray-300 transition-all duration-200 group-hover:border-primary-400 group-hover:scale-105 group-hover:shadow-md"
                      style={{ backgroundColor: localStroke }}
                    />
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                      <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400" />
                    </div>
                  </div>
                  <input
                    type="color"
                    value={localStroke}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="absolute opacity-0 w-0 h-0 pointer-events-none"
                    title="Border Color"
                  />
                </label>
              </div>
            </div>

            {/* Stroke Width */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MdLineWeight className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Width</span>
                </div>
                <span className="text-sm font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                  {border.strokeWidth}px
                </span>
              </div>
              <input
                type="range"
                name="strokeWidth"
                min={1}
                max={20}
                value={border.strokeWidth}
                onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
            </div>

            {/* Corner Radius */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TbBorderCornerPill className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Radius</span>
                </div>
                <span className="text-sm font-bold text-accent-600 bg-accent-50 px-2 py-0.5 rounded">
                  {border.cornerRadius}px
                </span>
              </div>
              <input
                type="range"
                name="cornerRadius"
                min={0}
                max={100}
                value={border.cornerRadius}
                onChange={(e) => onCornerRadiusChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent-500"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
